import { cookiePersistStorage } from "@/helper/common.helper";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  Error,
  FromEdit,
  IAllowed,
  IApiError,
  IConfigAndPayload,
  IFrom,
  IsRequest,
  StepError,
  useApiParamsType,
} from "@/types/stateTypes";
import { getObjSize } from "@/helper/helper";
import { toast } from "react-toastify";
import { VIEWER_ACCESS_ERROR_TEXT } from "@/constant";
import { IGenerateResult, ISvgContent } from "@/types/qr.types";

export const useInsufficientFundsAlert = create<{
  isError: boolean;
  setError: (error: boolean) => void;
}>((set) => ({
  isError: false,
  setError: (val) => {
    set({
      isError: val,
    });
  },
}));

export const useConfig = create<IConfigAndPayload>((set) => ({
  config: {},
  payload: null,
  files: {},
  isLoading: false,
  setIsLoading: (isLoading) => {
    set({ isLoading });
  },
  setFiles: (val) => {
    set({ files: val });
  },
  setConfig: (val) => {
    set({
      config: val,
    });
  },
  setPayload: (val) => {
    set({
      payload: val,
    });
  },
}));

export const useEdit = create<FromEdit>((set) => ({
  isEdit: false,
  setIsEdit: (val) => {
    set({
      isEdit: val,
    });
  },
}));

export const useError = create<Error>((set) => ({
  error: null,
  hasStepError: false,
  setError: (val) => set({ error: val }),
  setHasStepError: (val = true) => set({ hasStepError: val }),

  setDeleteError: (val) =>
    set((state) => {
      delete state.error?.[val];

      if (getObjSize(state.error) === 0) {
        return { error: false };
      }

      return { ...state };
    }),
  clearError: () => {
    set({
      error: null,
      hasStepError: false,
    });
  },
}));

export const useStepError = create<StepError>((set) => ({
  stepError: null,
  setStepError: (val) => set({ stepError: val }),
}));

export const useIsRequest = create<IsRequest>((set) => ({
  isRequest: false,
  setIsRequest: (val) => set(() => ({ isRequest: val })),
}));

export const useAllowed = create<IAllowed>((set) => ({
  isNotAllowed: "notAllowed",
  setIsNotAllowed: (val) => {
    set({
      isNotAllowed: val,
    });

    if (val === "allowed") {
      toast.error(VIEWER_ACCESS_ERROR_TEXT, {
        position: "bottom-right",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          background: "#E45541",
          color: "#ffffff",
        },
      });
    }
  },
}));

export const useApiError = create<IApiError>((set) => ({
  apiError: null,
  setIsApiError: (val) => {
    set({
      apiError: val,
    });
  },
  destroyApiError: () => {
    set({
      apiError: null,
    });
  },
}));

export const useFrom = create<IFrom>((set) => ({
  from: null,
  select: null,
  setFrom: (val) => {
    set({
      from: val,
    });
  },
  setSelect: (val) => {
    set({
      select: val,
    });
  },
  clean: () => {
    set({
      from: null,
      select: null,
    });
  },
}));

export const useQrSvgContent = create<ISvgContent<any>>((set) => ({
  svgContent: null,
  setSvgContent: (val) => {
    set({
      svgContent: val,
    });
  },
  destroySvgContent: () =>
    set({
      svgContent: null,
    }),
}));

export const useGenerateResult = create<IGenerateResult<any>>((set) => ({
  result: null,
  setGenerateResult: (val) => {
    let newObj = val;
    if (val && val.data) {
      newObj = {
        ...val,
        data: {
          ...val.data,
        },
      };
    }

    set({
      result: newObj,
    });
  },
  destroyGenerateResult: () =>
    set({
      result: null,
    }),
}));

export const useApiParams = create<useApiParamsType>((set) => ({
  apiParams: {
    perPage: 10,
    sort_by: "id",
    sort_type: "desc",
  },
  filterName: {
    title: "All",
    icon: "",
  },
  bulkDownload: false,
  fromNav: false,
  setApiParams: (val) => {
    set((state) => {
      return { ...state, apiParams: { ...state.apiParams, ...val } };
    });
  },
  setFromNav: (val) => {
    set({
      fromNav: val,
    });
  },
  setFolderId: (val) => {
    set((state) => {
      return {
        ...state,
        apiParams: {
          ...state.apiParams,
          folder_id: [...state.apiParams.folder_id, val],
        },
      };
    });
  },
  setFilterName: (val) => {
    set((state) => {
      return { ...state, filterName: val };
    });
  },
  setBulkStatus: (val) => {
    set({
      bulkDownload: val,
    });
  },
  resetApiParams: () => {
    set({
      apiParams: {
        perPage: 10,
        sort_by: "id",
        sort_type: "desc",
      },
    });
  },
}));

export const useAvatarState = create(
  persist<{ scale: number; setScale: (scale: number) => void }>(
    (set) => ({
      scale: 1.4,
      setScale: (scale) => set({ scale }),
    }),
    {
      name: "avatar",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useAlertState = create(
  persist<{
    alert: boolean;
    setAlert: (alert: boolean) => void;
  }>(
    (set) => ({
      alert: true,
      setAlert: (alert) => {
        set({ alert });
      },
    }),
    {
      name: "alert",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useShowRenewalSubscriptionPopup = create(
  persist<{
    seenRetryPopup: boolean;
    setSeenRetryPopup: (val: boolean) => void;
    showRetryPopup: boolean;
    setShowRetryPopup: (val: boolean) => void;
    destroyShowRenewalSubscriptionPopup: () => void;
  }>(
    (set) => ({
      seenRetryPopup: false,
      showRetryPopup: false,
      setSeenRetryPopup: (val) => {
        set({ seenRetryPopup: val });
      },
      setShowRetryPopup: (val) => {
        set({ showRetryPopup: val });
      },
      destroyShowRenewalSubscriptionPopup: () => {
        set({
          seenRetryPopup: false,
          showRetryPopup: false,
        });
      },
    }),
    {
      name: "useShowRenewalSubscriptionPopup",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useExistPopupState = create<{
  isExistTry: boolean;
  setIsExistTry: (alert: boolean) => void;
}>((set) => ({
  isExistTry: false,
  setIsExistTry: (isExistTry) => {
    set({ isExistTry });
  },
}));
